import React, { useEffect, useState } from 'react';
import { PieChart, Pie, Cell, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { fetchUninstallReasons } from "../../../services/apiService";

const UninstallReasonsPieChart = ({ productId, title, meta_key, group_by_period, start_date, end_date }) => {
    const [chartData, setChartData] = useState([]);
    const [reasonData, setReasonData] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                setChartData([]); // Clear existing chart data
                setReasonData([]); // Clear existing reason data

                // Call the fetchUninstallReasons from apiService with necessary parameters
                const data = await fetchUninstallReasons(productId, { meta_key, group_by_period, start_date, end_date });

                // Normalize "Other"/"Others" to a single label and ensure we have valid reason_detail
                const normalizeReason = (reason) => {
                    if (reason == null || String(reason).trim() === "") return "Others";
                    const r = String(reason).trim();
                    if (/^other(s)?$/i.test(r)) return "Others";
                    return r;
                };

                // Calculate the total count of uninstallations (use same total for percentage so "Others" can be included)
                const total = data.reduce((acc, item) => acc + (item.total || 0), 0) || 1;

                // Aggregate in case API returns both "Other" and "Others"
                const aggregated = data.reduce((acc, item) => {
                    const label = normalizeReason(item.reason_detail);
                    const count = item.total || 0;
                    acc[label] = (acc[label] || 0) + count;
                    return acc;
                }, {});

                // Build chart and table data from aggregated reasons
                const chartRows = Object.entries(aggregated).map(([reason_detail, count]) => ({
                    reason_detail,
                    value: parseFloat(((count / total) * 100).toFixed(2)),
                }));

                // Ensure "Others" is always present in pie chart and table
                const hasOthers = chartRows.some((row) => /^others$/i.test(row.reason_detail));
                if (!hasOthers) {
                    chartRows.push({ reason_detail: "Others", value: 0 });
                }

                setChartData(chartRows);

                const percentageReasonData = chartRows.map((row) => ({
                    reason_detail: row.reason_detail,
                    total: row.value.toFixed(2),
                }));
                setReasonData(percentageReasonData);
            } catch (error) {
                console.error('Error fetching uninstallation reasons', error);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [productId, meta_key, group_by_period, start_date, end_date]);

    const COLORS = ['#FF6384', '#36A2EB', '#FFCE56', '#4BC0C0', '#9966FF', '#FF9F40', '#C9DE00'];

    // Function to format the label
    const renderLabel = ({ value }) => {
        return `${value}%`;
    };

    // Function to format the tooltip
    const CustomTooltip = ({ active, payload }) => {
        if (active && payload && payload.length) {
            const item = payload[0];
            const reason = item.name || item.payload?.reason_detail || 'Unknown';
            const percent = item.value ?? item.payload?.value ?? 0;

            return (
                <div className="custom-tooltip">
                    <p>{`${reason} : ${percent}%`}</p>
                </div>
            );
        }
        return null;
    };

    return (
        <div>
            <div className="telemetry-block-container" style={{ width: '100%', height: '100%' }}>
                <div className="telemetry-block-column">
                    <div className="telemetry-block-header green-badge">
                        <span className="badge"></span>
                    </div>
                    <h3>{title}</h3>
                    <div style={{ width: '100%', height: 350 }}>
                        <ResponsiveContainer>
                            {chartData.length > 0 ? (
                                <PieChart>
                                    <Pie
                                        data={chartData}
                                        dataKey="value"
                                        nameKey="reason_detail"
                                        cx="50%"
                                        cy="50%"
                                        outerRadius={120}
                                        fill="#8884d8"
                                        label={renderLabel}
                                    >
                                        {chartData.map((entry, index) => (
                                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                        ))}
                                    </Pie>
                                    <Tooltip content={<CustomTooltip />} />
                                </PieChart>
                            ) : (
                                <p>Loading chart...</p>
                            )}
                        </ResponsiveContainer>
                    </div>
                </div>
                <div className="telemetry-block-column">
                    <div className="table-container">
                        <table>
                            <thead>
                                <tr>
                                    <th>Uninstallation Reasons</th>
                                    <th>%</th>
                                </tr>
                            </thead>
                            <tbody>
                                {reasonData.map((item, index) => (
                                    <tr key={index}>
                                        <td>
                                            <span className="badge" style={{ backgroundColor: COLORS[index % COLORS.length] }}></span>&nbsp;&nbsp;
                                            {item.reason_detail}
                                        </td>
                                        <td>{item.total}%</td>
                                    </tr>
                                ))}

                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default UninstallReasonsPieChart;
